import { useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Pill } from "@/components/Pill";
import { MessageList, ReplyBox } from "@/components/Conversation";
import { fmtDateLong } from "@/lib/constants";

function Row({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5 py-2.5 sm:flex-row sm:gap-4">
      <dt className="w-40 shrink-0 text-xs font-bold uppercase tracking-wider text-zinc-500">{label}</dt>
      <dd className="text-base font-semibold text-black">{value}</dd>
    </div>
  );
}

export function StatusLookup() {
  const [ticketId, setTicketId] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ticket, setTicket] = useState(null);
  const [replying, setReplying] = useState(false);

  const sendReply = async (text, clear) => {
    setReplying(true);
    try {
      const { data } = await api.post("/tickets/reply", { ticket_id: ticketId.trim(), contact: email.trim(), text });
      setTicket(data);
      clear();
      toast.success("Message sent to the team");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setReplying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setTicket(null);
    try {
      const { data } = await api.get("/tickets/lookup", { params: { ticket_id: ticketId.trim(), contact: email.trim() } });
      setTicket(data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="brut-card brut-shadow p-5 sm:p-8 fade-up" data-testid="status-lookup">
      <h2 className="font-display text-2xl font-bold">Check your ticket status</h2>
      <p className="mt-1 text-base text-zinc-600">Enter your ticket number <strong>and</strong> the email you used. Both are required and must match.</p>
      <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-[1fr_1.4fr_auto]">
        <div>
          <label className="field-label">Ticket No</label>
          <input className="field-input font-mono uppercase" placeholder="VS-0001" value={ticketId} onChange={(e) => setTicketId(e.target.value)} required data-testid="lookup-ticket-input" />
        </div>
        <div>
          <label className="field-label">Email used on the ticket</label>
          <input type="email" className="field-input" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="lookup-contact-input" />
        </div>
        <button type="submit" className="btn-primary self-end" disabled={busy} data-testid="lookup-submit-button">
          <Search className="h-4 w-4" /> {busy ? "Checking…" : "Check"}
        </button>
      </form>
      {error && <p className="mt-4 rounded-lg border-2 border-[#DC2626] bg-[#FEF2F2] px-3 py-2 text-sm font-semibold text-[#B91C1C]" data-testid="lookup-error">{error}</p>}
      {ticket && (
        <div className="mt-6 overflow-hidden rounded-xl border-2 border-black fade-up" data-testid="lookup-result">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-black bg-[#FEF9C3] px-4 py-3">
            <span className="font-mono text-2xl font-bold" data-testid="lookup-ticket-id">{ticket.ticket_id}</span>
            <Pill kind="status" value={ticket.status} className="!text-sm !px-3 !py-1.5" testId="lookup-status" />
          </div>
          <div className="px-4 py-2">
            <p className="py-2 text-base font-medium" data-testid="lookup-message">{ticket.message}</p>
            <dl className="divide-y divide-zinc-200">
              <Row label="Product" value={ticket.product} />
              <Row label="Query" value={ticket.query} />
              <Row label="Raised on" value={fmtDateLong(ticket.created_at)} />
              {ticket.handled_by && <Row label="Handled by" value={ticket.handled_by} />}
              {ticket.status === "Resolved" ? (
                <Row label="Resolved on" value={fmtDateLong(ticket.resolved_at)} />
              ) : (
                <Row label="Expected resolution by" value={fmtDateLong(ticket.sla_due_at)} />
              )}
            </dl>
          </div>
          <div className="border-t-2 border-black bg-white px-4 py-4" data-testid="customer-conversation">
            <h3 className="font-display text-lg font-bold">Send us a message</h3>
            <p className="mb-3 text-sm text-zinc-600">Want to add details? Write below or simply reply to any of our emails — it goes straight into your ticket{ticket.status === "Resolved" ? " and reopens it" : ""}.</p>
            <MessageList messages={ticket.messages} viewer="customer" testId="customer-conversation-list" />
            <div className="mt-3">
              <ReplyBox onSend={sendReply} busy={replying} placeholder="Type your message to the support team…" testId="customer-reply" label="Send message" />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
